import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Alert,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import RestoreIcon   from "@mui/icons-material/Restore";
import api from "../../utils/api";
import { ThemeContext } from "../../utils/ThemeContext";
import UserActions from "./UserActions";
import UserFormDialog from "./UserFormDialog";
import UserDeleteDialog from "./UserDeleteDialog";
import BulkInviteButton from "./BulkInviteButton";
import UserResetPasswordDialog from "./UserResetPasswordDialog";
import UserResetSuccessDialog from "./UserResetSuccessDialog";
import UserPermissionOverridesDialog from "./UserPermissionOverridesDialog";

const AllUsersTable = () => {
  const { isDark } = useContext(ThemeContext);
  const [activeTab, setActiveTab] = useState(0); // 0=active, 1=inactive
  const [users, setUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [reactivating, setReactivating] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [resetResult, setResetResult] = useState(null);
  const [fetchError, setFetchError] = useState("");
  const [permissionsUser, setPermissionsUser] = useState(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  useEffect(() => {
    const source = activeTab === 1 ? inactiveUsers : users;
    setFilteredUsers(
      source.filter((user) =>
        ["Username", "Name", "Lastname", "Email"]
          .map((key) => user[key]?.toLowerCase())
          .some((value) => value && value.includes(searchTerm.toLowerCase()))
      )
    );
    setSelectedUsers([]); // clear selection when switching tabs
  }, [users, inactiveUsers, searchTerm, activeTab]);

  const parseUserRows = (rows) => {
    const userMap = new Map();
    rows.forEach((row) => {
      if (!userMap.has(row.Username)) {
        userMap.set(row.Username, {
          Username: row.Username,
          UserID: row.UserID,
          Name: row.Name,
          Lastname: row.Lastname,
          Email: row.Email,
          Status: row.status || row.Status,
          AssociatedAccounts: Array.isArray(row.AssociatedAccounts)
            ? row.AssociatedAccounts
            : row.AssociatedAccounts
            ? String(row.AssociatedAccounts).split(",").map((a) => a.trim())
            : [],
          Roles: [],
        });
      }
      userMap.get(row.Username).Roles.push({ RoleID: row.RoleID, RoleName: row.RoleName });
    });
    return Array.from(userMap.values()).filter((u) => u.Roles.some((r) => r.RoleID !== 3));
  };

  const loadUsers = async () => {
    try {
      const [activeRes, inactiveRes] = await Promise.all([
        api.get("/users-by-role"),
        api.get("/users-by-role?status=inactive"),
      ]);
      setUsers(parseUserRows(activeRes.data));
      setInactiveUsers(parseUserRows(inactiveRes.data));
    } catch (err) {
      console.error("Failed to fetch users", err);
      setFetchError("Failed to load users. Please refresh.");
    }
  };

  const handleReactivate = async (username) => {
    if (!window.confirm(`Reactivate account for ${username}?`)) return;
    setReactivating(username);
    try {
      await api.put(`/users/${username}/activate`);
      await loadUsers();
    } catch (err) {
      console.error("Reactivate failed", err);
    } finally {
      setReactivating(null);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await api.get("/roles");
      setAvailableRoles(response.data);
    } catch (err) {
      console.error("Failed to fetch roles", err);
    }
  };

  const handleEditUser = (user) => {
    setFormData({
      ...user,
      Roles: user.Roles?.map((r) => r.RoleID) || [],
    });
    setFormMode("edit");
    setShowForm(true);
  };

  const handleSubmitUser = async (submittedData) => {
  try {
    if (formMode === "edit") {
      await api.put(`/users/${submittedData.UserID}`, submittedData);
    } else {
      if (!submittedData.Password || !submittedData.Username || !submittedData.Roles?.length) {
        alert("Username, password, and at least one role are required.");
        return;
      }

      await api.post("/addUser", {
        username: submittedData.Username,
        password: submittedData.Password,
        email: submittedData.Email || "default@example.com",
        name: submittedData.Name || "",
        lastname: submittedData.Lastname || "",
        roles: submittedData.Roles,
        sendInvitationEmail: submittedData.SendInvite || false,
        AssociatedAccounts: submittedData.AssociatedAccounts || [],
      });
    }

    setShowForm(false);
    loadUsers();
  } catch (error) {
    console.error("Error submitting user:", error);
    alert("Failed to submit user.");
  }
};

  const handleResetPassword = (username) => {
    setUserToReset(username);
    setShowResetDialog(true);
  };

  const confirmResetPassword = async () => {
    try {
      const res = await api.post(`/users/${userToReset}/resetPassword`);
      setResetResult({
        username: userToReset,
        password: res.data.temporaryPassword,
      });
    } catch (err) {
      console.error("Failed to reset password:", err);
      alert("Failed to reset password.");
    } finally {
      setShowResetDialog(false);
    }
  };

  const handleDeleteUser = (username) => {
    setUserToDelete(username);
    setShowDeleteDialog(true);
  };

  const handleSelectUser = (username) => {
    setSelectedUsers((prev) =>
      prev.includes(username)
        ? prev.filter((u) => u !== username)
        : [...prev, username]
    );
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allUsernames = filteredUsers.map((user) => user.Username);
      setSelectedUsers(allUsernames);
    } else {
      setSelectedUsers([]);
    }
  };

  const isAllSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((user) => selectedUsers.includes(user.Username));

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const getStatusChip = (status) => {
    const isActive = status?.toLowerCase() === "active";

    return (
      <Chip
        label={status || "Unknown"}
        size="small"
        sx={{
          borderRadius: "8px",
          fontWeight: 700,
          backgroundColor: isActive
            ? "rgba(46,125,50,0.10)"
            : "rgba(211,47,47,0.10)",
          color: isActive ? "#2e7d32" : "#d32f2f",
        }}
      />
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: isDark ? "#dde5f0" : "#223142",
              mb: 0.5,
            }}
          >
            Users
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: isDark ? "#8a9ab8" : "#6b778c",
            }}
          >
            Search, manage, invite, and maintain user accounts.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
          }}
        >
          <TextField
            label="Search Users"
            size="small"
            fullWidth
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              ...fieldSx,
              minWidth: { xs: "100%", md: 280 },
              maxWidth: { xs: "100%", md: 380 },
            }}
          />

          <Button
            variant="contained"
            sx={primaryButtonSx}
            onClick={() => {
              setFormData({
                Username: "",
                Password: "",
                Name: "",
                Lastname: "",
                Email: "",
                Roles: [],
                SendInvite: true,
              });
              setFormMode("add");
              setShowForm(true);
            }}
          >
            New User
          </Button>

          {activeTab === 0 && (
            <BulkInviteButton
              selectedUsers={selectedUsers}
              allUsers={users}
              onSuccess={() => { loadUsers(); setSelectedUsers([]); }}
            />
          )}
        </Box>
      </Box>

      {/* Active / Inactive tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600, minHeight: 40, fontSize: "0.85rem" },
          "& .Mui-selected": { color: "#2f63ad !important" },
          "& .MuiTabs-indicator": { backgroundColor: "#2f63ad" },
        }}
      >
        <Tab label={`Active Users (${users.length})`} />
        <Tab
          label={`Inactive Users (${inactiveUsers.length})`}
          icon={<PersonOffIcon sx={{ fontSize: 16 }} />}
          iconPosition="start"
          sx={{ color: inactiveUsers.length > 0 ? "#d97706 !important" : undefined }}
        />
      </Tabs>

      <TableContainer
        component={Paper}
        sx={{
          display: { xs: "none", lg: "block" },
          width: "100%",
          overflowX: "auto",
          overflowY: "visible",
          borderRadius: "16px",
          border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          backgroundColor: isDark ? "#16223a" : "rgba(255,255,255,0.96)",
        }}
      >
        <Table
          size="small"
          sx={{
            minWidth: 860,
            width: "100%",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{
                  backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#eef2fb",
                  width: 50,
                  borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                }}
              >
                <Checkbox
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  sx={{ p: 0.5 }}
                />
              </TableCell>

              {[
                "Username",
                "Name",
                "Lastname",
                "Email",
                "Associated Accounts",
                "Roles",
                "Status",
                "Actions",
              ].map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    fontWeight: 700,
                    backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#eef2fb",
                    color: isDark ? "#dde5f0" : "#223142",
                    whiteSpace: "nowrap",
                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredUsers.length > 0 ? (
              filteredUsers.map((user) => (
                <TableRow key={user.Username} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUsers.includes(user.Username)}
                      onChange={() => handleSelectUser(user.Username)}
                      color="primary"
                      sx={{ p: 0.5 }}
                    />
                  </TableCell>

                  <TableCell>{user.Username}</TableCell>
                  <TableCell>{user.Name}</TableCell>
                  <TableCell>{user.Lastname}</TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 240,
                    }}
                  >
                    {user.Email}
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 220,
                    }}
                  >
                    {user.AssociatedAccounts?.join(", ") || "None"}
                  </TableCell>

                  <TableCell
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      maxWidth: 220,
                    }}
                  >
                    {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
                  </TableCell>

                  <TableCell>{getStatusChip(user.Status)}</TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {activeTab === 1 ? (
                        <Tooltip title="Reactivate this user">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
                            disabled={reactivating === user.Username}
                            onClick={() => handleReactivate(user.Username)}
                            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, color: "#16a34a", borderColor: "#16a34a", "&:hover": { backgroundColor: "rgba(22,163,74,0.06)" } }}
                          >
                            {reactivating === user.Username ? "Reactivating…" : "Reactivate"}
                          </Button>
                        </Tooltip>
                      ) : (
                        <UserActions
                          user={user}
                          onEdit={handleEditUser}
                          onResetPassword={handleResetPassword}
                          onDelete={handleDeleteUser}
                          onManagePermissions={setPermissionsUser}
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ color: "text.secondary" }}>
                  No users found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: { xs: "block", lg: "none" } }}>
        {filteredUsers.length > 0 ? (
          filteredUsers.map((user) => (
            <Paper
              key={user.Username}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: "16px",
                border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: isDark ? "#1e2e47" : "rgba(255,255,255,0.96)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: 1.5,
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Checkbox
                    checked={selectedUsers.includes(user.Username)}
                    onChange={() => handleSelectUser(user.Username)}
                    color="primary"
                  />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {user.Username}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6b778c" }}>
                      {user.Name} {user.Lastname}
                    </Typography>
                  </Box>
                </Box>

                {getStatusChip(user.Status)}
              </Box>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {user.Email}
              </Typography>

              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Accounts:</strong>{" "}
                {user.AssociatedAccounts?.join(", ") || "None"}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Roles:</strong>{" "}
                {user.Roles?.map((r) => r.RoleName).join(", ") || "No Roles"}
              </Typography>

              <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                <UserActions
                  user={user}
                  onEdit={handleEditUser}
                  onResetPassword={handleResetPassword}
                  onDelete={handleDeleteUser}
                />
              </Box>
            </Paper>
          ))
        ) : (
          <Paper
            sx={{
              p: 2.5,
              borderRadius: "16px",
              border: "1px solid rgba(47,99,173,0.08)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
              backgroundColor: "rgba(255,255,255,0.96)",
            }}
          >
            <Typography sx={{ color: "text.secondary" }}>
              No users found.
            </Typography>
          </Paper>
        )}
      </Box>

      <UserFormDialog
        open={showForm}
        mode={formMode}
        formData={formData}
        setFormData={setFormData}
        onClose={() => setShowForm(false)}
        onSubmit={handleSubmitUser}
        reloadUsers={loadUsers}
        availableRoles={availableRoles}
        showAssociatedAccount={true}
      />

      <UserResetPasswordDialog
        open={showResetDialog}
        username={userToReset}
        onClose={() => setShowResetDialog(false)}
        onConfirm={confirmResetPassword}
      />

      <UserResetSuccessDialog
        open={!!resetResult}
        username={resetResult?.username}
        password={resetResult?.password}
        onClose={() => setResetResult(null)}
      />

      <UserDeleteDialog
        open={showDeleteDialog}
        username={userToDelete}
        onClose={() => setShowDeleteDialog(false)}
        onDelete={async () => {
          try {
            await api.put(`/users/${userToDelete}/deactivate`);
            setShowDeleteDialog(false);
            loadUsers();
          } catch (err) {
            console.error("Failed to deactivate user:", err);
            alert("Failed to deactivate user.");
          }
        }}
      />

      <UserPermissionOverridesDialog
        open={!!permissionsUser}
        user={permissionsUser}
        onClose={() => setPermissionsUser(null)}
      />
    </Box>
  );
};

export default AllUsersTable;
